import { escapeHtml } from '@colplat/ui-web';
import { apiFetch } from '../api.js';

/**
 * Transaction tags, on the client — the chips, and the calls behind them.
 *
 * A tag names one *event* in the ledger ("renovation", "reimbursable") and
 * hangs off a transaction. A `purpose` names a standing concern and hangs off
 * an *account*. They look alike on screen and are not interchangeable: only
 * the account-shaped one can say which legs of a transaction a filter counts,
 * which is why a tag-filtered report needs its own rule — see
 * `transactionTag` in the manifest, and `COUNTED_UNDER_TAG` in
 * services/api/src/webledger-reports.ts.
 *
 * Shared by the editor (assigning), the reports filter (selecting) and the
 * manager (naming), because three copies of "what a tag chip looks like" is
 * three places for them to drift apart.
 */

export interface TagDto { id: string; name: string; sortOrder: number }
export interface TransactionTagDto { id: string; transactionId: string; tagId: string }

/**
 * One tag, as a chip.
 *
 * `removable` adds the × the editor needs; the manager and the filter render
 * the same chip without it, so a tag looks like itself everywhere it appears.
 * The name carries the meaning — there is no colour here, deliberately
 * (invariant #11): a tag distinguished only by hue is a tag some readers
 * cannot tell from another.
 */
/**
 * `removeId` is whatever the caller wants handed back when the × is pressed.
 * It used to be the link's id, from when a chip removed the row immediately;
 * now that assignment is deferred to save it is the TAG's id, because a tag
 * chosen in this session has no link yet.
 */
export function tagChipHtml(tag: TagDto, opts: { removable?: boolean; removeId?: string } = {}): string {
  const remove = opts.removable && opts.removeId
    ? `<button type="button" class="wl-tag__x" data-tag-remove="${escapeHtml(opts.removeId)}"
        aria-label="Remove tag ${escapeHtml(tag.name)}" title="Remove">&times;</button>`
    : '';
  return `<span class="wl-tag" data-tag="${escapeHtml(tag.id)}">${escapeHtml(tag.name)}${remove}</span>`;
}

export async function loadTags(token: string): Promise<TagDto[]> {
  const res = await apiFetch('/apps/webledger/tags', { token }).catch(() => undefined);
  if (!res?.ok) return [];
  return ((await res.json()) as { tags: TagDto[] }).tags;
}

/** Every assignment, or just one transaction's when `transactionId` is given. */
export async function loadTransactionTags(
  token: string, transactionId?: string,
): Promise<TransactionTagDto[]> {
  const query = transactionId ? `?transactionId=${encodeURIComponent(transactionId)}` : '';
  const res = await apiFetch(`/apps/webledger/transaction-tags${query}`, { token }).catch(() => undefined);
  if (!res?.ok) return [];
  return ((await res.json()) as { transactionTags: TransactionTagDto[] }).transactionTags;
}

/*
 * ---------------------------------------------------------------- assigning
 *
 * One control for "which tags are on this transaction", used by the full
 * editor and by the wallets page's short form. It was two: the editor's chips
 * and dropdown, and a row of toggle chips on the short form copied from the
 * tag FILTER sitting above it. A filter and an assignment look alike and are
 * not — toggling a vocabulary is what a filter is for — and two controls for
 * one idea is how the pair drifts.
 *
 * Deferred, both of them. A tag change is applied when the transaction is
 * saved and discarded when it is cancelled, like every other field on those
 * forms. The editor used to write them the instant they were clicked, which
 * meant removing a tag and pressing Cancel left the tag removed.
 *
 * It also means a tag can be chosen on a transaction that does not exist yet:
 * the links are written after the transaction is, on the id it was minted
 * with (invariant #8).
 */

/** What the form is holding: the tags wanted, and the links already on record. */
export interface TagSelection {
  /** Tag ids the form currently shows as on. */
  wanted: Set<string>;
  /** tagId -> link id, for the ones that already exist and may need removing. */
  links: Map<string, string>;
}

export function emptyTagSelection(): TagSelection {
  return { wanted: new Set(), links: new Map() };
}

/** Load a transaction's tags into a selection, replacing whatever was there. */
export async function loadTagSelection(
  token: string, transactionId: string, into: TagSelection,
  /**
   * The links as the local copy has them, for when the server cannot be
   * asked. Without it an entry opened offline showed no tags at all — and
   * the local copy also carries tag changes saved here and not sent yet.
   */
  local?: () => ReadonlyArray<{ id: string; tagId: string }>,
): Promise<void> {
  into.wanted.clear();
  into.links.clear();
  const res = await apiFetch(`/apps/webledger/transaction-tags?transactionId=${encodeURIComponent(transactionId)}`, { token })
    .catch(() => undefined);
  const links = res?.ok
    ? ((await res.json()) as { transactionTags: TransactionTagDto[] }).transactionTags
    : (local?.() ?? []);
  for (const link of links) {
    into.wanted.add(link.tagId);
    into.links.set(link.tagId, link.id);
  }
}

/**
 * Paint the assigned chips and refill the "Add a tag…" picker with the rest.
 *
 * The chip's × carries the TAG id, not the link id: nothing is written until
 * save, so a tag chosen in this session has no link to name.
 */
export function renderTagSelection(
  listEl: HTMLElement, pickEl: HTMLSelectElement,
  allTags: readonly TagDto[], selection: TagSelection, readOnly = false,
): void {
  const chosen = allTags.filter((t) => selection.wanted.has(t.id));
  listEl.innerHTML = chosen.length === 0
    ? '<span class="text-secondary small">None.</span>'
    : chosen.map((t) => tagChipHtml(t, { removable: !readOnly, removeId: t.id })).join('');

  const available = allTags.filter((t) => !selection.wanted.has(t.id));
  pickEl.hidden = readOnly || available.length === 0;
  pickEl.innerHTML = '<option value="">Add a tag…</option>'
    + available.map((t) => `<option value="${escapeHtml(t.id)}">${escapeHtml(t.name)}</option>`).join('');
}

/** Wire the picker and the chips' × to the selection. `onChange` repaints. */
export function wireTagSelection(
  listEl: HTMLElement, pickEl: HTMLSelectElement, selection: TagSelection, onChange: () => void,
): void {
  pickEl.addEventListener('change', () => {
    const id = pickEl.value;
    pickEl.value = '';
    if (!id) return;
    selection.wanted.add(id);
    onChange();
  });
  listEl.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-tag-remove]');
    if (!button) return;
    event.preventDefault();
    selection.wanted.delete(button.dataset['tagRemove']!);
    onChange();
  });
}

/**
 * The selection as members of the transaction's own save.
 *
 * Ids are minted here (invariant #8), so what goes to the server is a list of
 * operations rather than a wish: create this link with this id, delete that
 * one. The server puts them in the same commit group as the transaction and
 * its splits, which is what makes Save one operation — atomic, queueable
 * offline as a single document, and held or applied as a whole if somebody
 * else changed the transaction meanwhile.
 *
 * A tag taken off and put back on is a NEW row, not the old one resurrected:
 * "tagged, untagged, tagged again" is three things that happened, and the
 * unique index that used to forbid it now constrains only living rows.
 */
export function tagSelectionMembers(
  selection: TagSelection,
): Array<{ id: string; op: 'create' | 'delete'; tagId?: string }> {
  const members: Array<{ id: string; op: 'create' | 'delete'; tagId?: string }> = [];
  for (const [tagId, linkId] of selection.links) {
    if (!selection.wanted.has(tagId)) members.push({ id: linkId, op: 'delete' });
  }
  for (const tagId of selection.wanted) {
    if (!selection.links.has(tagId)) members.push({ id: crypto.randomUUID(), op: 'create', tagId });
  }
  return members;
}
